import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { Baseurl } from "../../config/BaseUrl";

const initialState = {
  salonHomeCartItems: [],
  salonHomeSelectedCategory: " ",
  salonHomeCartTotalQuantity: 0,
  salonHomeCartTotalAmount: 0,
  salonHomeNetPayable: 0,
  salonHomeCartTotalMrp: 0,
  salonHomeCartTotalPrice: 0,
  salonHomeCartPackQuantity: 0,
  CouponDiscountAmount: 0,
  salonHomeLogisticsAmount: 0,
  salonHomeCartloading: false, // Changed to false initially
  productUpdated: false,
  salonHomeCheckoutData: "",
  loading: false,
  error: null,
};

// Web-compatible localStorage helper functions
const getLocalStorage = (key) => {
  if (typeof window !== "undefined") {
    try {
      return JSON.parse(localStorage.getItem(key)) || null;
    } catch (error) {
      console.error(`Error reading ${key} from localStorage:`, error);
      return null;
    }
  }
  return null;
};

const setLocalStorage = (key, value) => {
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error saving ${key} to localStorage:`, error);
    }
  }
};

const removeLocalStorage = (key) => {
  if (typeof window !== "undefined") {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing ${key} from localStorage:`, error);
    }
  }
};

// Async Thunks for API calls
export const fetchSalonHomeCartFromBackend = createAsyncThunk(
  "salonHomeCart/fetchSalonHomeCartFromBackend",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const clientId = state.Authentication.clientid;

      console.log("🏠 Fetching salon home cart - clientId:", clientId);

      // Check if user is authenticated
      if (!clientId) {
        console.log("🏠 No authenticated user, skipping salon home cart fetch");
        // Initialize from localStorage for guest users
        const localCart = getLocalStorage("salonHomeCartItems") || [];
        const localCategory =
          getLocalStorage("salonHomeSelectedCategory") || "";

        return {
          success: true,
          data: {
            salonHomeCartItems: localCart,
            salonHomeCartTotalAmount: localCart.reduce(
              (total, item) => total + (item.TotalAmount || 0),
              0
            ),
            salonHomeNetPayable: localCart.reduce(
              (total, item) => total + (item.TotalAmount || 0),
              0
            ),
            salonHomeCartTotalMrp: localCart.reduce(
              (total, item) => total + (item.TotalMrp || 0),
              0
            ),
            salonHomeCartTotalQuantity: localCart.reduce(
              (total, item) => total + (item.Qty || 0),
              0
            ),
            salonHomeCartPackQuantity: localCart.length,
          },
        };
      }

      console.log(
        "🏠 Making API call to fetch salon home cart for client:",
        clientId
      );

      const response = await axios.get(
        `${Baseurl}/api/v1/cart/salonClient/${clientId}`
      );

      console.log("✅ Salon home cart API response received");
      return response.data;
    } catch (error) {
      console.error("❌ Salon home cart fetch error:", error);

      // Fallback to localStorage on error
      const localCart = getLocalStorage("salonHomeCartItems") || [];
      return {
        success: true,
        data: {
          salonHomeCartItems: localCart,
          salonHomeCartTotalAmount: localCart.reduce(
            (total, item) => total + (item.TotalAmount || 0),
            0
          ),
          salonHomeNetPayable: localCart.reduce(
            (total, item) => total + (item.TotalAmount || 0),
            0
          ),
          salonHomeCartTotalMrp: localCart.reduce(
            (total, item) => total + (item.TotalMrp || 0),
            0
          ),
          salonHomeCartTotalQuantity: localCart.reduce(
            (total, item) => total + (item.Qty || 0),
            0
          ),
          salonHomeCartPackQuantity: localCart.length,
        },
      };
    }
  }
);

export const addToSalonHomeCartBackend = createAsyncThunk(
  "salonHomeCart/addToSalonHomeCartBackend",
  async (itemData, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const clientId = state.Authentication.clientid;

      console.log("🏠 Adding to salon home cart - clientId:", clientId);

      if (!clientId) {
        return rejectWithValue("No authenticated user");
      }

      // Prepare the data with clientId for salon home cart
      const cartData = {
        ...itemData,
        clientId: clientId,
      };

      console.log("🏠 Sending to salon home cart backend");

      const response = await axios.post(
        `${Baseurl}/api/v1/cart/salon-home/new`,
        cartData
      );

      console.log("✅ Add to salon home cart successful");
      return response.data;
    } catch (error) {
      console.log(
        "❌ Add to salon home cart error:",
        error.response?.data || error.message
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const removeFromSalonHomeCartBackend = createAsyncThunk(
  "salonHomeCart/removeFromSalonHomeCartBackend",
  async (productId, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const clientId = state.Authentication.clientid;

      console.log(
        "🏠 Removing from salon home cart - clientId:",
        clientId,
        "productId:",
        productId
      );

      if (!clientId) {
        return rejectWithValue("No authenticated user");
      }

      const response = await axios.delete(
        `${Baseurl}/api/v1/cart/salon-home/remove/${productId}?clientId=${clientId}`
      );

      console.log("✅ Remove from salon home cart successful");
      return response.data;
    } catch (error) {
      console.log("❌ Remove from salon home cart error:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateSalonHomeCartItemBackend = createAsyncThunk(
  "salonHomeCart/updateSalonHomeCartItemBackend",
  async ({ productId, quantity }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const clientId = state.Authentication.clientid;

      console.log(
        "🏠 Updating salon home cart item - clientId:",
        clientId,
        "productId:",
        productId,
        "quantity:",
        quantity
      );

      if (!clientId) {
        return rejectWithValue("No authenticated user");
      }

      const response = await axios.put(
        `${Baseurl}/api/v1/cart/salon-home/update/${productId}`,
        {
          quantity,
          clientId,
        }
      );

      console.log("✅ Update salon home cart item successful");
      return response.data;
    } catch (error) {
      console.log("❌ Update salon home cart item error:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const clearSalonHomeCartBackend = createAsyncThunk(
  "salonHomeCart/clearSalonHomeCartBackend",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const clientId = state.Authentication.clientid;

      console.log("🏠 Clearing salon home cart - clientId:", clientId);

      if (!clientId) {
        return rejectWithValue("No authenticated user");
      }

      const response = await axios.delete(
        `${Baseurl}/api/v1/cart/salon-home/clear?clientId=${clientId}`
      );

      console.log("✅ Clear salon home cart successful");
      return response.data;
    } catch (error) {
      console.log("❌ Clear salon home cart error:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const SalonHomeCartSlice = createSlice({
  name: "SalonHomeCart",
  initialState,
  reducers: {
    setSelectedSalonHomeCategory(state, action) {
      state.salonHomeSelectedCategory = action.payload;
      setLocalStorage(
        "salonHomeSelectedCategory",
        state.salonHomeSelectedCategory
      );
    },
    setSalonHomeCheckoutAllData(state, action) {
      state.salonHomeCheckoutData = action.payload;
    },
    salonHomeCartReplaceClick(state, action) {
      const tempProducts = action.payload;
      state.salonHomeCartItems = [action.payload];
      state.salonHomeCartTotalQuantity++;
      state.salonHomeCartPackQuantity++;

      state.salonHomeCartTotalAmount =
        Number(state.salonHomeCartTotalAmount) + Number(action.payload.Price);

      if (state.salonHomeCartTotalAmount >= 400) {
        state.salonHomeLogisticsAmount = 0;
      } else {
        state.salonHomeLogisticsAmount = 40;
      }
      state.salonHomeNetPayable =
        Number(state.salonHomeCartTotalAmount) +
        Number(state.salonHomeLogisticsAmount);

      state.salonHomeCartloading = !state.salonHomeCartloading;
      setLocalStorage("salonHomeCartItems", state.salonHomeCartItems);
    },

    // Local cart actions (for immediate UI update)
    addtoSalonHomeCart(state, action) {
      let itemIndex = -1;
      itemIndex = state.salonHomeCartItems.findIndex(
        (item) => item.ProductId === action.payload.ProductId
      );
      if (itemIndex >= 0) {
        state.salonHomeCartTotalQuantity++;
        const item = state.salonHomeCartItems[itemIndex];
        item.Qty += 1;
        item.TotalMrp = item.Mrp * item.Qty;
        item.TotalPrice = item.Price * item.Qty;
        item.TotalAmount = item.Price * item.Qty;
        state.salonHomeCartTotalAmount += item.Price;
        state.salonHomeNetPayable = state.salonHomeCartTotalAmount;
      } else {
        const tempProduct = action.payload;
        state.salonHomeCartItems.push(tempProduct);
        state.salonHomeCartTotalQuantity++;
        state.salonHomeCartPackQuantity++;
        state.salonHomeCartTotalAmount += tempProduct.Price;
        state.salonHomeNetPayable = state.salonHomeCartTotalAmount;
      }
      state.salonHomeCartloading = !state.salonHomeCartloading;
      setLocalStorage("salonHomeCartItems", state.salonHomeCartItems);
    },
    decrementSalonHomeCart(state, action) {
      let itemIndex = -1;
      itemIndex = state.salonHomeCartItems.findIndex(
        (item) => item.ProductId === action.payload.ProductId
      );

      if (itemIndex >= 0) {
        if (state.salonHomeCartItems[itemIndex].Qty > 1) {
          let quant = state.salonHomeCartItems[itemIndex].Qty - 1;
          state.salonHomeCartItems[itemIndex].Qty =
            state.salonHomeCartItems[itemIndex].Qty - 1;
          state.salonHomeCartItems[itemIndex].TotalMrp =
            Number(state.salonHomeCartItems[itemIndex].Mrp) * Number(quant);

          state.salonHomeCartItems[itemIndex].TotalPrice =
            Number(state.salonHomeCartItems[itemIndex].Price) * Number(quant);
          state.salonHomeCartItems[itemIndex].TotalAmount =
            Number(state.salonHomeCartItems[itemIndex].Price) * Number(quant);
        } else if (state.salonHomeCartItems[itemIndex].Qty === 1) {
          const nextCartItems = state.salonHomeCartItems.filter(
            (cartItem) => cartItem.ProductId !== action.payload.ProductId
          );
          state.salonHomeCartPackQuantity--;
          state.salonHomeCartItems = nextCartItems;
        }

        state.salonHomeCartTotalAmount -= action.payload.Price;
        state.salonHomeNetPayable = state.salonHomeCartTotalAmount;
        state.salonHomeCartloading = !state.salonHomeCartloading;
      }
      setLocalStorage("salonHomeCartItems", state.salonHomeCartItems);
    },
    removefromSalonHomeCart(state, action) {
      const nextCartItems = state.salonHomeCartItems.filter(
        (cartItem) => cartItem.ProductId !== action.payload.ProductId
      );
      state.salonHomeCartPackQuantity--;
      state.salonHomeCartItems = nextCartItems;
      state.salonHomeCartloading = !state.salonHomeCartloading;
      setLocalStorage("salonHomeCartItems", state.salonHomeCartItems);
    },
    getSalonHomeCartTotal(state, action) {
      state.salonHomeCartTotalAmount = 0;
      state.salonHomeNetPayable = 0;
      state.salonHomeCartTotalMrp = 0;
      state.salonHomeCartTotalPrice = 0;
      const totalitem = state.salonHomeCartItems;

      for (let i = 0; i < totalitem.length; i++) {
        state.salonHomeCartTotalAmount =
          Number(state.salonHomeCartTotalAmount) +
          Number(state.salonHomeCartItems[i].TotalAmount);

        state.salonHomeCartTotalMrp =
          Number(state.salonHomeCartTotalMrp) +
          Number(state.salonHomeCartItems[i].TotalMrp);
        state.salonHomeCartTotalPrice =
          Number(state.salonHomeCartTotalPrice) +
          Number(state.salonHomeCartItems[i].TotalPrice);
      }

      state.salonHomeNetPayable = Number(state.salonHomeCartTotalAmount);
    },
    clearSalonHomeCart(state, action) {
      state.salonHomeCartItems = [];
      state.salonHomeCartTotalQuantity = 0;
      state.salonHomeCartTotalAmount = 0;
      state.salonHomeNetPayable = 0;
      state.salonHomeCartTotalMrp = 0;
      state.salonHomeCartPackQuantity = 0;
      state.salonHomeCartloading = !state.salonHomeCartloading;
      setLocalStorage("salonHomeCartItems", state.salonHomeCartItems);
    },

    // New action to initialize cart from localStorage
    initializeSalonHomeCartFromLocalStorage(state) {
      const savedCartItems = getLocalStorage("salonHomeCartItems");
      const savedSelectedCategory = getLocalStorage(
        "salonHomeSelectedCategory"
      );

      if (savedCartItems) {
        state.salonHomeCartItems = savedCartItems;
        // Recalculate totals from saved cart items
        state.salonHomeCartTotalQuantity = savedCartItems.reduce(
          (total, item) => total + item.Qty,
          0
        );
        state.salonHomeCartTotalAmount = savedCartItems.reduce(
          (total, item) => total + (item.TotalAmount || 0),
          0
        );
        state.salonHomeNetPayable = state.salonHomeCartTotalAmount;
        state.salonHomeCartTotalMrp = savedCartItems.reduce(
          (total, item) => total + (item.TotalMrp || 0),
          0
        );
        state.salonHomeCartPackQuantity = savedCartItems.length;
      }

      if (savedSelectedCategory) {
        state.salonHomeSelectedCategory = savedSelectedCategory;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Cart
      .addCase(fetchSalonHomeCartFromBackend.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSalonHomeCartFromBackend.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          const cartData = action.payload.data;

          state.salonHomeCartItems = cartData.salonHomeCartItems || [];
          state.salonHomeCartTotalAmount =
            cartData.salonHomeCartTotalAmount || 0;
          state.salonHomeNetPayable = cartData.salonHomeNetPayable || 0;
          state.salonHomeCartTotalMrp = cartData.salonHomeCartTotalMrp || 0;
          state.salonHomeCartTotalQuantity =
            cartData.salonHomeCartTotalQuantity || 0;
          state.salonHomeCartPackQuantity =
            cartData.salonHomeCartPackQuantity || 0;

          console.log("✅ Salon Home Cart loaded successfully:", {
            items: state.salonHomeCartItems.length,
            totalAmount: state.salonHomeCartTotalAmount,
            totalQuantity: state.salonHomeCartTotalQuantity,
          });

          setLocalStorage("salonHomeCartItems", state.salonHomeCartItems);
        }
      })
      .addCase(fetchSalonHomeCartFromBackend.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        console.log("❌ Failed to fetch salon home cart:", state.error);
      })
      // Add to Cart
      .addCase(addToSalonHomeCartBackend.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToSalonHomeCartBackend.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          const cartData = action.payload.data;

          state.salonHomeCartItems = cartData.salonHomeCartItems || [];
          state.salonHomeCartTotalAmount =
            cartData.salonHomeCartTotalAmount || 0;
          state.salonHomeNetPayable = cartData.salonHomeNetPayable || 0;
          state.salonHomeCartTotalMrp = cartData.salonHomeCartTotalMrp || 0;
          state.salonHomeCartTotalQuantity =
            cartData.salonHomeCartTotalQuantity || 0;
          state.salonHomeCartPackQuantity =
            cartData.salonHomeCartPackQuantity || 0;

          console.log("✅ Item added to salon home cart:", {
            items: state.salonHomeCartItems.length,
            totalAmount: state.salonHomeCartTotalAmount,
          });

          setLocalStorage("salonHomeCartItems", state.salonHomeCartItems);
        }
      })
      .addCase(addToSalonHomeCartBackend.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        console.log("❌ Failed to add to salon home cart:", state.error);
      })
      // Remove from Cart
      .addCase(removeFromSalonHomeCartBackend.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFromSalonHomeCartBackend.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          const cartData = action.payload.data;

          state.salonHomeCartItems = cartData.salonHomeCartItems || [];
          state.salonHomeCartTotalAmount =
            cartData.salonHomeCartTotalAmount || 0;
          state.salonHomeNetPayable = cartData.salonHomeNetPayable || 0;
          state.salonHomeCartTotalMrp = cartData.salonHomeCartTotalMrp || 0;
          state.salonHomeCartTotalQuantity =
            cartData.salonHomeCartTotalQuantity || 0;
          state.salonHomeCartPackQuantity =
            cartData.salonHomeCartPackQuantity || 0;

          console.log("✅ Item removed from salon home cart");

          setLocalStorage("salonHomeCartItems", state.salonHomeCartItems);
        }
      })
      .addCase(removeFromSalonHomeCartBackend.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        console.log("❌ Failed to remove from salon home cart:", state.error);
      })
      // Update Cart Item
      .addCase(updateSalonHomeCartItemBackend.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSalonHomeCartItemBackend.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          const cartData = action.payload.data;

          state.salonHomeCartItems = cartData.salonHomeCartItems || [];
          state.salonHomeCartTotalAmount =
            cartData.salonHomeCartTotalAmount || 0;
          state.salonHomeNetPayable = cartData.salonHomeNetPayable || 0;
          state.salonHomeCartTotalMrp = cartData.salonHomeCartTotalMrp || 0;
          state.salonHomeCartTotalQuantity =
            cartData.salonHomeCartTotalQuantity || 0;
          state.salonHomeCartPackQuantity =
            cartData.salonHomeCartPackQuantity || 0;

          console.log("✅ Salon home cart item updated");

          setLocalStorage("salonHomeCartItems", state.salonHomeCartItems);
        }
      })
      .addCase(updateSalonHomeCartItemBackend.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        console.log("❌ Failed to update salon home cart item:", state.error);
      })
      // Clear Cart
      .addCase(clearSalonHomeCartBackend.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clearSalonHomeCartBackend.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.salonHomeCartItems = [];
          state.salonHomeCartTotalQuantity = 0;
          state.salonHomeCartTotalAmount = 0;
          state.salonHomeNetPayable = 0;
          state.salonHomeCartTotalMrp = 0;
          state.salonHomeCartPackQuantity = 0;

          console.log("✅ Salon home cart cleared");

          setLocalStorage("salonHomeCartItems", state.salonHomeCartItems);
        }
      })
      .addCase(clearSalonHomeCartBackend.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        console.log("❌ Failed to clear salon home cart:", state.error);
      });
  },
});

export const {
  setSelectedSalonHomeCategory,
  setSalonHomeCheckoutAllData,
  salonHomeCartReplaceClick,
  addtoSalonHomeCart,
  decrementSalonHomeCart,
  removefromSalonHomeCart,
  getSalonHomeCartTotal,
  clearSalonHomeCart,
  initializeSalonHomeCartFromLocalStorage,
} = SalonHomeCartSlice.actions;

export default SalonHomeCartSlice.reducer;
